// Builds the child process env: system allowlist forwarded from parent env
// (only if present, limited to the named keys below) + exactly 6 mandatory
// AVATAR_* vars always set. The allowlist shape itself guarantees ANTHROPIC_*
// never gets forwarded from parent env (it copies only the listed keys). Also
// exports a standalone guard (`assertNoAnthropicEnv`) so both a builder-level
// caller AND session-runner (test 41/42) can enforce "fail spawn if the
// effective-config env overlay carries ANTHROPIC_*" — a pure function that
// throws, callable in isolation on whatever candidate env object is at hand.
export interface MandatoryAvatarEnv {
  missionId: string;
  ticketId: string;
  runId: string;
  repoRoot: string; // must be absolute
  // H12 (2026-09-29): no `resultPath` / AVATAR_RESULT_PATH — the terminal
  // result event is the only result source; no controller code ever read a
  // result file, and the worker has no tool that could read env anyway.
}

// D06 finding B (real S1 smoke on macOS): the original 6-key allowlist
// (PATH HOME LANG LC_ALL TMPDIR SHELL) omitted USER, and macOS's
// Keychain-backed `claude` login lookup requires USER in the child env —
// without it every real spawn fails auth (misclassified as auth_expired)
// regardless of binding/hook correctness. Added USER + LOGNAME/TERM for the
// same reason (standard POSIX identity/terminal vars, not secrets, already
// implicitly trusted since HOME/SHELL are forwarded) — never ANTHROPIC_*/
// CLAUDE_CODE_USE_* (guaranteed by this allowlist only ever copying the
// named keys below, see assertNoAnthropicEnv for the config-overlay side).
const SYSTEM_ALLOWLIST_KEYS = [
  "PATH",
  "HOME",
  "SHELL",
  "USER",
  "LOGNAME",
  "TMPDIR",
  "LANG",
  "LC_ALL",
  "TERM",
  // Re-review backlog 2026-09-26: corporate networks — the worker (`claude`)
  // must reach the API through the same proxy the parent shell uses, and a
  // TLS-intercepting proxy is useless without the custom CA bundle path. The
  // CA vars are file paths; a proxy URL MAY embed basic-auth credentials —
  // they already live in the parent shell at the same trust tier as
  // HOME/SHELL and only reach the worker on this machine. Still never
  // ANTHROPIC_*/CLAUDE_CODE_USE_*. Lowercase too: Linux tooling sets
  // `https_proxy`.
  "HTTP_PROXY",
  "HTTPS_PROXY",
  "NO_PROXY",
  "http_proxy",
  "https_proxy",
  "no_proxy",
  "NODE_EXTRA_CA_CERTS",
  "SSL_CERT_FILE",
] as const;

export class ContractError extends Error {
  readonly code = "contract_error";
  constructor(message: string) {
    super(message);
    this.name = "ContractError";
  }
}

// Throws if any key in the candidate env object(s) starts with "ANTHROPIC_".
// Called both at builder level (guarding config-provided overlay) and by
// session-runner directly (test 41/42) so the check is testable in isolation.
export function assertNoAnthropicEnv(candidate: Record<string, string | undefined>): void {
  const offending = Object.keys(candidate).filter((k) => k.startsWith("ANTHROPIC_"));
  if (offending.length > 0) {
    throw new ContractError(
      `child-env-allowlist-builder: biến ANTHROPIC_* bị cấm forward vào child (${offending.join(", ")}).`,
    );
  }
}

/**
 * I3 (spec V2 §3, §4.2): worker không bao giờ được CẤP giá trị secret. Builder
 * không đổi hành vi — đây chỉ là chốt chặn: env con mang ĐÚNG một known-value
 * (`test.env`/stdin, registry) ⇒ ném, thông điệp chỉ nêu tên key. So bằng nhau
 * (không substring) để biến hệ thống vô tình chứa chuỗi trùng không chặn oan.
 * Review 04b M1: chỉ ném khi giá trị KHÔNG truy được về env cha mà builder chép
 * (`env[key] !== parentEnv[key]`) — tức có code cấp từ nguồn khác. Trùng giá
 * trị shell (vd proxy/CA ghi cả trong `test.env`) là trùng ngẫu nhiên: giá trị
 * đã ở shell cùng tầng tin cậy như v1; doctor cảnh báo theo tên key.
 */
export function assertNoKnownSecretValuesInChildEnv(
  env: Record<string, string>,
  knownValues: readonly string[],
  /** Vắng ⇒ không giá trị nào truy được về cha ⇒ mọi lần khớp đều ném. */
  parentEnv: Readonly<Record<string, string | undefined>> = {},
): void {
  if (knownValues.length === 0) return;
  const known = new Set(knownValues);
  const offending = Object.keys(env).filter(
    (key) => known.has(env[key] as string) && env[key] !== parentEnv[key],
  );
  if (offending.length > 0) {
    throw new ContractError(
      `child-env-allowlist-builder: env con mang giá trị secret của mission (${offending.join(", ")}) — I3.`,
    );
  }
}

export function buildChildEnvAllowlist(
  parentEnv: NodeJS.ProcessEnv,
  mandatory: MandatoryAvatarEnv,
): Record<string, string> {
  if (!mandatory.repoRoot.startsWith("/")) {
    throw new ContractError(
      `child-env-allowlist-builder: AVATAR_REPO_ROOT phải là đường dẫn tuyệt đối, nhận "${mandatory.repoRoot}".`,
    );
  }

  // NOTE: parentEnv (the supervisor's own process env, e.g. a developer's
  // shell) is intentionally NOT scanned for ANTHROPIC_* here — a developer's
  // shell commonly carries ANTHROPIC_API_KEY for unrelated tools, and the
  // system allowlist below only ever copies the listed keys anyway, so
  // ANTHROPIC_* from parentEnv can never leak through this builder. The
  // "fail spawn if config carries ANTHROPIC_*" rule (§6.2) is about the
  // effective-config env overlay a caller may merge in — callers must run
  // `assertNoAnthropicEnv` on that overlay (session-runner does, for the
  // config-derived candidate env, not the raw parent env — test 41/42).
  const env: Record<string, string> = {};
  for (const key of SYSTEM_ALLOWLIST_KEYS) {
    const value = parentEnv[key];
    if (value !== undefined) env[key] = value;
  }

  env.AVATAR_AUTOPILOT = "1";
  env.AVATAR_NON_INTERACTIVE = "1";
  env.AVATAR_MISSION_ID = mandatory.missionId;
  env.AVATAR_TICKET_ID = mandatory.ticketId;
  env.AVATAR_RUN_ID = mandatory.runId;
  env.AVATAR_REPO_ROOT = mandatory.repoRoot;

  return env;
}
